import type { SpeechVoiceDescriptor } from "../speech-out";

export interface DeviceVoice {
  readonly default: boolean;
  readonly lang: string;
  readonly localService: boolean;
  readonly name: string;
  readonly voiceURI: string;
}

export function describeVoices(
  voices: readonly DeviceVoice[],
): readonly SpeechVoiceDescriptor[] {
  return voices
    .map((voice) => ({
      id: voice.voiceURI,
      name: voice.name,
      language: voice.lang,
      isLocal: voice.localService,
      isDefault: voice.default,
      label: `${voice.name} — ${voice.lang} — ${voice.localService ? "local" : "network"}`,
    }))
    .sort((left, right) => {
      if (left.isLocal !== right.isLocal) return left.isLocal ? -1 : 1;
      if (left.isDefault !== right.isDefault) return left.isDefault ? -1 : 1;
      return left.label.localeCompare(right.label);
    });
}

export function preferredOfflineVoice(
  voices: readonly SpeechVoiceDescriptor[],
): SpeechVoiceDescriptor | null {
  return (
    voices.find(
      ({ isLocal, language }) => isLocal && /^en(?:-|$)/i.test(language),
    ) ??
    voices.find(({ isLocal }) => isLocal) ??
    null
  );
}

export interface VoiceSource {
  addEventListener(type: "voiceschanged", listener: () => void): void;
  getVoices(): readonly DeviceVoice[];
  removeEventListener(type: "voiceschanged", listener: () => void): void;
}

export interface RetryTimer {
  clearTimeout(handle: number): void;
  setTimeout(callback: () => void, delayMs: number): number;
}

export function loadVoiceOptions(
  source: VoiceSource,
  timer: RetryTimer,
  maximumWaitMs = 2000,
  retryMs = 100,
): Promise<readonly SpeechVoiceDescriptor[]> {
  if (maximumWaitMs < 0 || retryMs <= 0) {
    throw new RangeError("voice retry timing must be positive");
  }
  return new Promise((resolve) => {
    let elapsedMs = 0;
    let handle: number | undefined;
    let settled = false;

    const finish = (voices: readonly DeviceVoice[]): void => {
      if (settled) return;
      settled = true;
      if (handle !== undefined) timer.clearTimeout(handle);
      source.removeEventListener("voiceschanged", changed);
      resolve(describeVoices(voices));
    };
    const check = (): void => {
      const voices = source.getVoices();
      if (voices.length > 0 || elapsedMs >= maximumWaitMs) {
        finish(voices);
        return;
      }
      elapsedMs += retryMs;
      handle = timer.setTimeout(check, retryMs);
    };
    const changed = (): void => check();

    source.addEventListener("voiceschanged", changed);
    check();
  });
}
